import { ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
    AnnualReportProvider,
    IEquityMovementScheduleDates,
    IEquityMovementScheduleFli,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumicoCoreLayoutProvider, EMPTY_ARRAY_LENGTH, FIRST_ELEMENT_INDEX } from '@modeso/aumico-lib-core-fe';
import {
    AnnualReportStep,
    IAnnualReport,
    IEquityMovementSchedule,
    IFinStatements,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep, isEqual } from 'lodash-es';
import { Observable, debounceTime, distinctUntilChanged, filter, find, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { DataState } from '../../../shared/enum/datastate.enum';
import { templateConstants } from '../../../shared/template-constants';
import { localizationKeys } from '../../../utils/localizationKeys';
import { AnnualReportSubPage } from '../annual-report-sub-page';
import { EquityMovementScheduleHelper } from '../helpers/equity-movement-schedule.helper';

@UntilDestroy()
@Component({
    selector: 'equity-movement-schedule-page',
    templateUrl: 'equity-movement-schedule.page.html',
    styleUrls: ['equity-movement-schedule.page.scss'],
})
export class AnnualReportEquityMovementSchedulePage extends AnnualReportSubPage implements OnInit {
    localizationKeys = localizationKeys;

    showNoFlisInEquityGroupErrorMessage = false;
    showCompleteDataErrorMessage = false;
    hasNoPreviousData = false;

    reportLocale: string | undefined;

    private isSaveRequestInProgress = false;
    private currentAnnualReport: IAnnualReport | undefined;
    private equityMovementScheduleToSave: IEquityMovementSchedule | null = null;
    private equityMovementSchedulePrefilledValue: IEquityMovementSchedule | undefined;
    equityMovementScheduleInputValue: IEquityMovementSchedule | null = null;

    private equityGroupFlis: IEquityMovementScheduleFli[] = [];
    equityMovementScheduleFlis: IEquityMovementScheduleFli[] = [];
    equityMovementScheduleDates: IEquityMovementScheduleDates | undefined;
    equityMovementScheduleStructureDiff: { newFliNames: string[]; removedFliNames: string[] } = {
        newFliNames: [],
        removedFliNames: [],
    };

    dataState: DataState = DataState.Loading;

    isPartOfArNavigation$: Observable<boolean>;


    private balanceAutofillFinStatements: IFinStatements[] | null = null;

    constructor(
        readonly injector: Injector,
        override readonly dialog: MatDialog,
        protected override readonly router: Router,
        private readonly annualReportProvider: AnnualReportProvider,
        public override readonly aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        protected override readonly actionsListener$: ActionsSubject,
        private readonly mixpanelService: MixpanelService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);

        this.currentAnnualReportStep = AnnualReportStep.EMS;

        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(AnnualReportStep.EMS);
    }

    override ngOnInit(): void {
        this.mixpanelService.time_event(MIXPANEL_EVENTS.EMS_TIME_SPENT_TILL_COMPLETION);
        super.ngOnInit();

        this.disableProceedButton = false;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;

        this.dataState = DataState.Loading;

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.providerOfAnnualReports.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });

        this.annualReportProvider
            .getActiveAnnualReport$()
            .pipe(
                debounceTime(500),
                withLatestFrom(this.annualReportProvider.getActiveFinStatementsWithReclasses$()),
                map(([activeAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) => {
                    if (
                        activeAnnualReport != null &&
                        activeAnnualReport.generalInformation != null &&
                        finStatements.length >= templateConstants.financialStatementMinimumLength &&
                        finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template != null &&
                        finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template != null
                    ) {
                        return [activeAnnualReport, finStatements] as [IAnnualReport, IFinStatements[]];
                    } else {
                        return [null, finStatements] as [IAnnualReport | null, IFinStatements[]];
                    }
                }),
                distinctUntilChanged((prev, curr) => isEqual(prev[0], curr[0])),
                tap(([activeAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) =>
                    this.handleGetActiveAnnualReport(activeAnnualReport, finStatements),
                ),
                filter(
                    ([activeAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) =>
                        activeAnnualReport != null && activeAnnualReport.equityMovementSchedule == null,
                ),
                switchMap(([activeAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) =>
                    this.annualReportProvider.getAnnualReportForBalanceAutoFill$().pipe(
                        withLatestFrom(this.annualReportProvider.getBalanceAutofillFinStatements$()),
                        take(1),
                    ),
                ),
                filter(
                    ([autoFilledAnnualReport, previousYearFinStatements]: [IAnnualReport, IFinStatements[]]) =>
                        autoFilledAnnualReport != null &&
                        previousYearFinStatements.length >= templateConstants.financialStatementMinimumLength &&
                        previousYearFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping
                            .template != null &&
                        previousYearFinStatements[templateConstants.incomeTemplate].templateAndAccountsMapping
                            .template != null &&
                        autoFilledAnnualReport.equityMovementSchedule != null,
                ),
                untilDestroyed(this),
            )
            .subscribe(
                ([autofillAnnualReport, previousYearFinStatements]: [IAnnualReport | null, IFinStatements[]]) => {
                    this.handleAnnualReportForAutoFill(autofillAnnualReport, previousYearFinStatements);
                },
            );

        this.annualReportProvider
            .getAnnualReportForBalanceAutoFill$()
            .pipe(
                withLatestFrom(this.annualReportProvider.getBalanceAutofillFinStatements$()),
                filter(
                    ([previousAnnualReport, previousYearFinStatements]: [IAnnualReport | null, IFinStatements[]]) =>
                        previousAnnualReport != null &&
                        previousAnnualReport.equityMovementSchedule != null &&
                        previousYearFinStatements.length >= templateConstants.financialStatementMinimumLength &&
                        previousYearFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping
                            .template != null,
                ),
                untilDestroyed(this),
            )
            .subscribe(([, previousYearFinStatements]: [IAnnualReport | null, IFinStatements[]]) => {
                this.balanceAutofillFinStatements = previousYearFinStatements;
                if (this.applyPreviousYearOpeningBalances()) {
                    this.equityMovementScheduleFlis = [...this.equityMovementScheduleFlis];
                }
            });

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());
        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());
    }

    private applyPreviousYearOpeningBalances(): boolean {
        if (this.balanceAutofillFinStatements == null || this.equityMovementScheduleFlis.length === 0) {
            return false;
        }
        const previousBalanceTemplate =
            this.balanceAutofillFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template;
        if (previousBalanceTemplate?.equityGroupName == null) {
            return false;
        }

        const previousEquityFlis = EquityMovementScheduleHelper.extractFlisFromEquityGroup(
            [previousBalanceTemplate.group],
            previousBalanceTemplate.equityGroupName,
        );
        const openingByFliName = new Map<string, number>(
            previousEquityFlis.map((fli) => [fli.fliName, fli.currentYearOpeningBalance]),
        );

        let hasChanged = false;
        this.equityMovementScheduleFlis.forEach((fli) => {
            const previousOpening = openingByFliName.get(fli.fliName);
            if (previousOpening != null && fli.previousYearOpeningBalance !== previousOpening) {
                fli.previousYearOpeningBalance = previousOpening;
                hasChanged = true;
            }
        });
        return hasChanged;
    }

    private forceRemountComponent(): void {
        this.dataState = DataState.Loading;
        this.cdr.detectChanges();
        this.dataState = DataState.Success;
    }

    private handleGetActiveAnnualReport(
        currentAnnualReport: IAnnualReport | null,
        currentFinStatements: IFinStatements[],
    ): void {
        this.equityGroupFlis = [];
        this.equityMovementScheduleFlis = [];
        this.equityMovementScheduleStructureDiff = { newFliNames: [], removedFliNames: [] };

        if (currentAnnualReport == null) {
            this.dataState = DataState.Error;
            this.showCompleteDataErrorMessage = true;
            return;
        }
        if (
            currentAnnualReport == null ||
            currentAnnualReport.generalInformation == null ||
            currentFinStatements.length < templateConstants.financialStatementMinimumLength ||
            currentFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template == null ||
            currentFinStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template == null
        ) {
            this.dataState = DataState.Error;

            throw new Error(
                'current annual report and general information and balance template and income template must not be equal null',
            );
        }


        this.showCompleteDataErrorMessage = false;
        this.currentAnnualReport = cloneDeep<IAnnualReport>(currentAnnualReport);
        this.reportLocale = currentAnnualReport.locale.toLowerCase();
        this.equityMovementScheduleInputValue = cloneDeep<IEquityMovementSchedule | null>(
            currentAnnualReport.equityMovementSchedule,
        );

        this.hasNoPreviousData = currentAnnualReport.generalInformation.noPreviousData;

        if (
            currentFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template
                .equityGroupName == null
        ) {
            this.dataState = DataState.Error;
            this.showNoFlisInEquityGroupErrorMessage = true;
            return;
        }

        this.equityGroupFlis = EquityMovementScheduleHelper.extractFlisFromEquityGroup(
            [currentFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template.group],
            currentFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template.equityGroupName,
        );

        const savedMatchingFlis = EquityMovementScheduleHelper.getMovementScheduleFliInputData(
            this.equityGroupFlis,
            currentAnnualReport.equityMovementSchedule,
        );
        const proposableMappingFlis = EquityMovementScheduleHelper.getMovementScheduleFliInputData(
            this.equityGroupFlis,
            null,
        );
        const savedFliNames = new Set<string>(savedMatchingFlis.map((fli) => fli.fliName));
        this.equityMovementScheduleFlis = [
            ...savedMatchingFlis,
            ...proposableMappingFlis.filter((fli) => !savedFliNames.has(fli.fliName)),
        ];

        if (this.equityMovementScheduleFlis.length === EMPTY_ARRAY_LENGTH) {
            this.dataState = DataState.Error;
            this.showNoFlisInEquityGroupErrorMessage = true;
            return;
        }
        if (this.equityMovementScheduleInputValue != null) {
            const normalizedEntries = EquityMovementScheduleHelper.normalizeEntries(
                this.equityMovementScheduleInputValue.equityMovementEntries,
            );
            this.equityMovementScheduleInputValue = {
                ...this.equityMovementScheduleInputValue,
                equityMovementEntries: normalizedEntries,
            };
            this.equityMovementScheduleStructureDiff = EquityMovementScheduleHelper.computeStructureDiff(
                normalizedEntries,
                proposableMappingFlis,
            );
        }

        this.equityMovementScheduleDates = EquityMovementScheduleHelper.prepareEquityMovementScheduleDates(
            currentAnnualReport.generalInformation,
            this.reportLocale,
        );

        this.applyPreviousYearOpeningBalances();

        this.forceRemountComponent();
    }

    private handleAnnualReportForAutoFill(
        previousAnnualReport: IAnnualReport | undefined,
        previousFinStatements: IFinStatements[],
    ): void {
        if (
            previousAnnualReport == null ||
            previousAnnualReport.generalInformation == null ||
            previousFinStatements.length < templateConstants.financialStatementMinimumLength ||
            previousFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template == null ||
            previousFinStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template == null ||
            previousAnnualReport.equityMovementSchedule == null
        ) {
            throw new Error(
                'current annual report, general information, balance template, income template and ems must not be equal null for autofill',
            );
        }

        const previousEquityGroupFlis = EquityMovementScheduleHelper.extractFlisFromEquityGroup(
            [previousFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template.group],
            previousFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template
                .equityGroupName,
        );

        const mergedFlis = EquityMovementScheduleHelper.getMovementScheduleFlisForAutofill(
            this.equityGroupFlis,
            previousEquityGroupFlis,
        );
        this.equityMovementScheduleFlis = mergedFlis;

        this.balanceAutofillFinStatements = previousFinStatements;
        this.applyPreviousYearOpeningBalances();

        const autofilledEquityMovementEntries = EquityMovementScheduleHelper.getAutofilledEquityMovementScheduleEntries(
            this.equityMovementScheduleFlis,
            previousAnnualReport.equityMovementSchedule.equityMovementEntries,
        );
        const normalizedAutofilledEntries =
            EquityMovementScheduleHelper.normalizeEntries(autofilledEquityMovementEntries);
        this.equityMovementScheduleInputValue = {
            equityMovementEntries: normalizedAutofilledEntries,
            textAdditionAboveEms: previousAnnualReport.equityMovementSchedule.textAdditionAboveEms,
            textAdditionBelowEms: previousAnnualReport.equityMovementSchedule.textAdditionBelowEms,
            // Shifted by one year: last report's CURRENT-year titles describe the same year that is
            // the PREVIOUS year here. The current year gets fresh titles from this report's dates.
            previousOpeningTitle: previousAnnualReport.equityMovementSchedule.currentOpeningTitle,
            previousClosingTitle: previousAnnualReport.equityMovementSchedule.currentClosingTitle,
        };
        this.equityMovementScheduleStructureDiff = EquityMovementScheduleHelper.computeStructureDiff(
            normalizedAutofilledEntries,
            EquityMovementScheduleHelper.getMovementScheduleFliInputData(this.equityGroupFlis, null),
        );

        this.forceRemountComponent();
    }

    onEquityMovementScheduleValuePrepared(equityMovementSchedule: IEquityMovementSchedule): void {
        this.equityMovementScheduleToSave = equityMovementSchedule;
    }

    onEquityMovementSchedulePrefilledValuePrepared(equityMovementSchedule: IEquityMovementSchedule): void {
        this.equityMovementSchedulePrefilledValue = equityMovementSchedule;
    }

    proceedButtonClicked = (): void => {
        this.save();
    };

    override save(): Observable<void> {
        this.mixpanelService.track(MIXPANEL_EVENTS.EMS_TIME_SPENT_TILL_COMPLETION);

        if (this.currentAnnualReport == null) {
            throw new Error('Current annual is null!');
        }

        this.currentAnnualReport.equityMovementSchedule =
            this.equityMovementScheduleToSave ??
            this.equityMovementSchedulePrefilledValue ??
            this.currentAnnualReport.equityMovementSchedule;
        this.isSaveRequestInProgress = true;
        this.dispatchSaveAnnualReport(this.currentAnnualReport);

        return of(void 0);
    }

    protected override isDataNewlyCreatedAndSaveable(): boolean {
        if (this.currentAnnualReport && this.currentAnnualReport.equityMovementSchedule == null && this.disableProceedButton === false) {
            return true;
        }
        return false;
    }

    protected override isDataChanged(): Observable<boolean> {
        if (this.currentAnnualReport == null) {
            return of(false);
        }
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else {
            if (this.equityMovementScheduleToSave == null) {
                return of(false);
            }

            const isEquityMovementScheduleEqualDefaultValue = fastDeepEqual(
                this.equityMovementScheduleToSave,
                this.equityMovementSchedulePrefilledValue,
            );

            if (isEquityMovementScheduleEqualDefaultValue) {
                return of(false);
            } else {
                const isCurrentEqualSavedEquityMovementSchedule = fastDeepEqual(
                    this.currentAnnualReport.equityMovementSchedule,
                    this.equityMovementScheduleToSave,
                );
                return of(!isCurrentEqualSavedEquityMovementSchedule);
            }
        }
    }

    protected onSaveSuccessfully(): void {
        if (this.isSaveRequestInProgress) {
            this.isSaveRequestInProgress = false;
            this.savedSuccessfully.next(true);
            this.fireSuccessNotification();
        }
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }
}